<template>
  <div>
    <h2 class="mb-5">Introduction to Titrimetry: Data Processing</h2>

    <p class="mb-3">
      a) Please fill in the table below with the titration data and calculations from your 3
      successful trials.
    </p>

    <p class="pl-n8 mb-5">
      <v-simple-table>
        <thead>
          <tr>
            <td style="font-weight: bold; text-align: left; width: 25%">Property</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 1</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 2</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 3</td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Mass of KHP (g)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massKHPT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massKHPT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massKHPT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Final Buret Reading (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Initial Buret Reading (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Volume of <chemical-latex content="NaOH" /> used (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Notes</td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT1"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT2"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT3"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><chemical-latex content="[NaOH] (M)" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concNaOHT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concNaOHT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concNaOHT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </p>

    <p class="mb-3 mt-4">
      b) What is the mean and standard deviation for <stemble-latex content="$\ce{[NaOH]}?$" />
    </p>

    <v-row class="mb-5">
      <v-col cols="5">
        <calculation-input
          v-model="inputs.meanConcNaOH"
          prepend-text="$\ce{[NaOH]:}$"
          :disabled="!allowEditing"
        />
      </v-col>
      <v-col cols="1" class="pr-n3 mr-n5 pt-2 mt-5">
        <stemble-latex content="$\pm$" />
      </v-col>
      <v-col cols="3">
        <calculation-input
          v-model="inputs.stDevConcNaOH"
          append-text="$\text{M}$"
          :disabled="!allowEditing"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import STextarea from '@/common/components/STextarea.vue';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';

export default {
  name: 'UNO_Lab12_ReportSheet',
  components: {
    ChemicalLatex,
    CalculationInput,
    StembleLatex,
    STextarea,
  },
  mixins: [DynamicQuestionMixin()],
  props: {
    isSubmitting: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        massKHPT1: null,
        VfT1: null,
        ViT1: null,
        volTitrantT1: null,
        notesT1: null,
        concNaOHT1: null,

        massKHPT2: null,
        VfT2: null,
        ViT2: null,
        volTitrantT2: null,
        notesT2: null,
        concNaOHT2: null,

        massKHPT3: null,
        VfT3: null,
        ViT3: null,
        volTitrantT3: null,
        notesT3: null,
        concNaOHT3: null,

        meanConcNaOH: null,
        stDevConcNaOH: null,
      },
    };
  },
};
</script>

<style></style>
